/**
 * Everything the session needs from sv_gateway, in one place.
 *
 * The wire is snake_case; the app is camelCase. Translating here — and only here —
 * keeps `account.full_name` from leaking into screens, where a rename on the server
 * would then have to be chased through the UI.
 *
 * Verified against the live gateway (api.sam-vision.uz, 2026-07-14):
 *   POST /auth/register     {email,password,full_name}  -> AuthTokenResponse (+account)
 *   POST /auth/login        {identifier,password}       -> AuthTokenResponse (+account)
 *   GET  /auth/me                                       -> profile, snake_case
 *   POST /auth/logout       {refresh_token}             -> {message}
 *   POST /companies/personal (no body)                  -> Company, kind:"personal"
 *   POST /companies         {name,organization_type}    -> Company, kind:"org"
 *   POST /me/link           {code}                      -> membership | 422
 */
import {request, requestMe, type Tokens} from '@/shared/api';
import type {Company} from '@/entities/session/contexts';

/** The signed-in human. `id` is the JWT `sub` — the key the context resolver needs. */
export type Account = {
  id: string;
  email: string;
  phone: string;
  username: string;
  fullName: string;
  avatarUrl: string;
  /**
   * Контур аккаунта (SP2): "release" | "beta". Несёт его только `/auth/me` —
   * login-ответ его НЕ содержит (AccountSummary на проводе беднее). Пусто ⇒
   * "release" (старый токен/старый гейтвей). Веб-сборка по нему маршрутизирует
   * себя на нужный поддомен; на нативе не используется (одноконтурная сборка).
   */
  contour: string;
  /**
   * Платформенный админ (SP2): сверка контура его не режет — он ходит в оба.
   * Веб не редиректит такого пользователя. Только из `/auth/me`; пусто ⇒ false.
   */
  isPlatformAdmin: boolean;
};

/** sv_gateway AuthTokenResponse. */
type AuthWire = {
  access_token: string;
  refresh_token: string;
  account?: AccountWire | null;
  /**
   * The account must change its password before it is allowed anywhere.
   *
   * Set when a platform administrator issued a new one through
   * `POST /users/{account_id}/reset-password`, which mails it in plain text. The
   * tokens that arrive alongside it are REAL and the server blocks nothing with
   * them — the whole restriction lives in the client, so a client that ignores this
   * field leaves a mailed password valid forever.
   *
   * Optional because every other issuer of this response omits it.
   */
  must_change_password?: boolean | null;
};

type AccountWire = {
  id: string;
  email?: string;
  phone?: string;
  username?: string;
  full_name?: string;
  avatar_url?: string;
  /** SP2 — только в ответе `/auth/me`; login-ответ этих полей не несёт. */
  contour?: string;
  is_platform_admin?: boolean;
};

export type Session = {
  tokens: Tokens;
  account: Account;
  /** See `AuthWire.must_change_password`. Absent on the wire ⇒ `false`. */
  mustChangePassword: boolean;
};

function toAccount(w: AccountWire): Account {
  return {
    id: w.id,
    email: w.email ?? '',
    phone: w.phone ?? '',
    username: w.username ?? '',
    fullName: w.full_name ?? '',
    avatarUrl: w.avatar_url ?? '',
    // Пусто (login-ответ / старый гейтвей) ⇒ release / не-админ. Так релиз-
    // кабинет, чей /auth/me контура не отдаёт, никого не редиректит.
    contour: w.contour ?? 'release',
    isPlatformAdmin: w.is_platform_admin ?? false,
  };
}

/**
 * The account block is optional in the DTO, so a session cannot be built without it.
 * Failing loudly beats carrying an empty accountId into the resolver, where it would
 * quietly match no company and drop the user into onboarding with a workspace he owns.
 */
function toSession(w: AuthWire): Session {
  if (!w.account?.id) {
    throw new Error('auth response carried no account');
  }
  return {
    tokens: {accessToken: w.access_token, refreshToken: w.refresh_token},
    account: toAccount(w.account),
    mustChangePassword: w.must_change_password === true,
  };
}

export async function login(
  identifier: string,
  password: string,
): Promise<Session> {
  const res = await request<AuthWire>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({identifier, password}),
  });
  return toSession(res);
}

/**
 * What the gateway decided a social credential meant.
 *
 * Four of the five are reachable; `linked` is not, and it is listed anyway because it
 * remains part of the response contract. It used to mean "attach this identity to the
 * account named in `account_id`" — on a route that requires NO credentials, so a
 * stranger's account id was the entire proof needed to take it over. The gateway now
 * rejects a non-empty `account_id` with a flat 400 before anything reaches sv_auth, so
 * the branch exists in the enum and cannot be produced. An exhaustive switch should
 * still handle it rather than crash on a value the contract permits.
 *
 * `unspecified` is not a fifth business outcome either: it is the gateway's defensive
 * default when the integer code maps to nothing it knows. There is no UI to design for
 * it beyond "something is wrong".
 */
export type OAuthOutcome =
  | 'logged_in'
  | 'auto_linked'
  | 'registered'
  | 'linked'
  | 'unspecified';

export type OAuthSession = Session & {outcome: OAuthOutcome};

/**
 * Trade a provider credential for a session.
 *
 * `account_id` is deliberately NOT sent, and must never be added: see the note on
 * `linked` above. The provider's own verified email is what links an existing account
 * (`auto_linked`); there is no other linking path through this route, by design.
 */
export async function oauthExchange(
  provider: string,
  credential: string,
): Promise<OAuthSession> {
  const res = await request<AuthWire & {outcome?: string}>('/auth/oauth/exchange', {
    method: 'POST',
    body: JSON.stringify({provider, credential}),
  });
  const outcome = (res.outcome ?? 'unspecified') as OAuthOutcome;
  return {...toSession(res), outcome};
}

export type RegisterInput = {
  email: string;
  password: string;
  fullName: string;
  /** «Принимаю соглашение и политику» — без отметки сервер ответит 422 terms_required. */
  acceptTerms: boolean;
  /** Редакция документов, которую человек видел. */
  termsVersion: string;
};

/** Регистрация ждёт кода из письма (202). Токенов здесь нет — их даёт подтверждение. */
export type PendingSignup = {
  email: string;
  expiresInSecs: number;
  resendAfterSecs: number;
};

type PendingWire = {email: string; expires_in_secs: number; resend_after_secs: number};

function toPending(w: PendingWire): PendingSignup {
  return {
    email: w.email,
    expiresInSecs: w.expires_in_secs,
    resendAfterSecs: w.resend_after_secs,
  };
}

export async function register(input: RegisterInput): Promise<PendingSignup> {
  const res = await request<PendingWire>('/auth/register', {
    method: 'POST',
    body: JSON.stringify({
      email: input.email,
      password: input.password,
      full_name: input.fullName,
      accept_terms: input.acceptTerms,
      terms_version: input.termsVersion,
    }),
  });
  return toPending(res);
}

/**
 * Код из письма → сессия, тот же ответ, что у входа. `password` — тот, что ввели в
 * форме: сервер сверяет его, чтобы чужая повторная регистрация не подменила пароль.
 */
export async function confirmRegistration(
  email: string,
  code: string,
  password: string,
): Promise<Session> {
  const res = await request<AuthWire>('/auth/register/confirm', {
    method: 'POST',
    body: JSON.stringify({email, code, password}),
  });
  return toSession(res);
}

/** «Отправить ещё раз». Для почты без ожидающей регистрации ответ тот же, письма нет. */
export async function resendRegistration(email: string): Promise<PendingSignup> {
  const res = await request<PendingWire>('/auth/register/resend', {
    method: 'POST',
    body: JSON.stringify({email}),
  });
  return toPending(res);
}

/** Who is the bearer of the token we just found in the Keychain? */
export async function fetchProfile(): Promise<Account> {
  const res = await request<AccountWire>('/auth/me');
  return toAccount(res);
}

/** Best-effort revoke. A dead refresh token on the server is the goal; a network
 *  failure here must still let the local session go. */
export async function revokeSession(refreshToken: string): Promise<void> {
  await request<unknown>('/auth/logout', {
    method: 'POST',
    body: JSON.stringify({refresh_token: refreshToken}),
  });
}

export async function forgotPassword(email: string): Promise<void> {
  await request<unknown>('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({email}),
  });
}

export async function resetPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  await request<unknown>('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({email, code, new_password: newPassword}),
  });
}

/** Idempotent: the gateway returns the existing personal company on a repeat call. */
export function ensurePersonalCompany(): Promise<Company> {
  return request<Company>('/companies/personal', {method: 'POST'});
}

/**
 * `organizationType` is a parameter and no longer the literal `'other'` it was hard-
 * coded to. That default was invisible and permanent: the settings screen printed the
 * sector read-only, so a company created here could never be anything else, and the
 * operator console — which filters and sorts the whole platform by this field — saw
 * every cabinet-created company in one bucket while looking entirely correct.
 * Vocabulary: `entities/company/orgType.ts`.
 */
export function createCompany(
  name: string,
  organizationType: string = 'other',
): Promise<Company> {
  return request<Company>('/companies', {
    method: 'POST',
    body: JSON.stringify({name, organization_type: organizationType}),
  });
}

/** Redeem an employer's one-time invite code. Identity comes from the JWT, so no
 *  X-Company-Id: the code itself says which company. */
export function linkByCode(code: string): Promise<unknown> {
  return requestMe<unknown>('/me/link', undefined, {
    method: 'POST',
    body: JSON.stringify({code}),
  });
}
